const isSSR = typeof window === 'undefined';

/**
 * Create a lazily-loaded module cache that starts fetching immediately on
 * the client but never blocks the main thread.  Returns a `get()` that
 * resolves to the extracted exports and a synchronous `ref` proxy object
 * whose properties are filled in once the import settles.
 *
 * Usage:
 *   const { ref, get } = lazyDeps(() => Promise.all([
 *       import('foo'),
 *       import('bar'),
 *   ]), ([foo, bar]) => ({
 *       doFoo: foo.doFoo,
 *       doBar: bar.default,
 *   }));
 *
 *   // synchronous access (undefined until loaded, safe for fire-and-forget analytics)
 *   ref.doFoo?.('arg');
 *
 *   // guaranteed access (for flows where the result matters)
 *   const { doFoo } = await get();
 */
export function lazyDeps(loader, extractor) {
    const ref = {};
    let _promise = null;

    function get() {
        if (!_promise) {
            if (isSSR) return Promise.resolve(ref);
            _promise = loader().then((modules) => {
                Object.assign(ref, extractor(modules));
                return ref;
            });
        }
        return _promise;
    }

    if (!isSSR) get();
    return { ref, get };
}
